import React, { useCallback, useEffect, useMemo, useState } from "react";

import HttpManager from "../../../../services/HttpManager";
import { CountEvent } from "../../../../services/v2/stockops.service";
import { Empty, Loading, apiError, ui } from "../kits/shared";

/*
	Historial del conteo (Esteban, 2026-10-28): todo lo que pasó, del más reciente al más viejo — quién escaneó, subió o bajó
	cantidades, capturó, dio de alta, descartó o renombró, y cuándo se mandó a firma, se firmó, se aplicó o se canceló.
	"Quedan" = cuántas quedaron después de ese cambio (vacío en movimientos viejos donde no se sabe).
*/

const PAGE = 200;

const KINDS: [string, string][] = [["all", "Todo"], ["up", "Subidas"], ["down", "Bajadas"], ["created", "Altas"], ["discard", "Descartes"], ["rename", "Nombres"], ["count", "Conteo"]];
const COUNT_ACTIONS = ["open", "submit", "sign", "reopen", "apply", "cancel", "photos"];

const kindOf = (event: CountEvent): string[] => {
	const kinds: string[] = [];
	if (COUNT_ACTIONS.includes(event.action)) kinds.push("count");
	if (["quick", "approve", "link"].includes(event.action)) kinds.push("created");
	if (event.action === "discard") kinds.push("discard");
	if (event.action === "rename") kinds.push("rename");
	if (event.delta > 0) kinds.push("up");
	if (event.delta < 0 && event.action !== "discard") kinds.push("down");
	return kinds;
};

const label = (event: CountEvent, first: boolean): string => {
	switch (event.action) {
		case "open": return "Abrió el conteo";
		case "scan": return "Escaneó";
		case "undo": return "Quitó";
		case "set": return "Cambió la cantidad";
		case "edit": return "Corrigió en la tabla";
		case "quick": return "Dio de alta y contó";
		case "link": return "Le puso código y contó";
		case "pending": return first ? "Capturó por dar de alta" : "Sumó a por dar de alta";
		case "approve": return "Dio de alta";
		case "discard": return "Descartó";
		case "rename": return "Cambió el nombre";
		case "dropped": return "Salió del conteo (producto dado de baja)";
		case "photos": return "Subió fotos del estante";
		case "submit": return "Mandó a firma";
		case "sign": return "Firmó";
		case "reopen": return "Regresó a contar";
		case "apply": return "Aplicó a existencias";
		case "cancel": return "Canceló el conteo";
		default: return event.action;
	}
};

const dayTitle = (day: string | null): string => {
	if (!day) return "Sin fecha";
	const text = new Date(day + "T12:00:00").toLocaleDateString("es-MX", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
	return text.split(" ").map((word) => word === "de" || word === "del" ? word : word.charAt(0).toUpperCase() + word.slice(1)).join(" ");
};

const HistorialConteo: React.FC<{ campusId: number; countId: number }> = ({ campusId, countId }) => {
	const http = HttpManager.getInstance().stockOpsService;
	const [events, setEvents] = useState<CountEvent[] | null>(null);
	const [people, setPeople] = useState<string[]>([]);
	const [error, setError] = useState<string | null>(null);
	const [busy, setBusy] = useState<boolean>(false);
	const [kind, setKind] = useState<string>("all");
	const [person, setPerson] = useState<string>("");
	const [search, setSearch] = useState<string>("");
	const [shown, setShown] = useState<number>(PAGE);

	const load = useCallback(() => {
		setBusy(true);
		setError(null);
		http.countHistory(campusId, countId).then((response) => { setEvents(response.data.events); setPeople(response.data.people); })
			.catch((requestError) => setError(apiError(requestError, "No se pudo cargar el historial.")))
			.finally(() => setBusy(false));
	}, [campusId, countId]);
	useEffect(() => { load(); }, [load]);

	// The oldest event of each "por dar de alta" is when it was captured; the rest add to it.
	const firstOfPending = useMemo(() => {
		const first: Record<number, number> = {};
		(events || []).forEach((event) => { if (event.product?.pending && event.action === "pending") first[event.product.id] = event.id; });
		return first;
	}, [events]);

	const filtered = useMemo(() => {
		const words = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
		return (events || []).filter((event) => (kind === "all" || kindOf(event).includes(kind))
			&& (!person || event.user === person)
			&& (words.length === 0 || words.every((word) => [event.product?.name, event.product?.code, event.product?.size, event.product?.color, event.detail]
				.some((text) => (text || "").toLowerCase().includes(word)))));
	}, [events, kind, person, search]);
	useEffect(() => { setShown(PAGE); }, [kind, person, search]);

	if (error && events === null) return <div className="alert alert-danger" style={{ marginTop: "12px", padding: "8px 12px", fontSize: "14px" }}>{error}</div>;
	if (events === null) return <Loading />;

	const added = filtered.reduce((sum, event) => sum + (event.delta > 0 ? event.delta : 0), 0);
	const removed = filtered.reduce((sum, event) => sum + (event.delta < 0 ? -event.delta : 0), 0);
	const visible = filtered.slice(0, shown);
	const days: { day: string | null; rows: CountEvent[] }[] = [];
	visible.forEach((event) => {
		const last = days[days.length - 1];
		if (last && last.day === event.day) last.rows.push(event); else days.push({ day: event.day, rows: [event] });
	});

	return <div style={{ marginTop: "12px" }}>
		<div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
			{KINDS.map(([key, text]) => <button key={key} type="button" className={"btn btn-sm rounded-pill " + (kind === key ? "btn-primary" : "btn-outline-secondary")} onClick={() => setKind(key)}>{text}</button>)}
		</div>
		<div style={{ display: "flex", gap: "8px 10px", flexWrap: "wrap", alignItems: "flex-end", marginTop: "10px" }}>
			<div style={{ ...ui.field("220px"), flex: "1 1 220px" }}><span style={ui.caption}>Quién</span>
				<select className="form-select" style={ui.input} value={person} onChange={(event) => setPerson(event.target.value)}>
					<option value="">Todas las personas</option>
					{people.map((name) => <option key={name} value={name}>{name}</option>)}
				</select></div>
			<div style={{ ...ui.field("220px"), flex: "1 1 220px" }}><span style={ui.caption}>Producto</span>
				<input type="text" className="form-control" style={ui.input} value={search} placeholder="Nombre o código…" onChange={(event) => setSearch(event.target.value)} /></div>
			<button type="button" className={ui.btn} disabled={busy} onClick={load}>{busy ? "Actualizando…" : "Actualizar"}</button>
		</div>
		<div style={{ ...ui.small, marginTop: "8px" }}>
			{filtered.length === 1 ? "1 movimiento" : filtered.length.toLocaleString("es-MX") + " movimientos"}
			{added > 0 ? " · +" + added.toLocaleString("es-MX") + " piezas" : ""}{removed > 0 ? " · −" + removed.toLocaleString("es-MX") + " piezas" : ""}
		</div>
		{error ? <div className="alert alert-danger" style={{ marginTop: "8px", padding: "8px 12px", fontSize: "14px" }}>{error}</div> : null}
		{filtered.length === 0 ? <Empty text="No hay movimientos con estos filtros." /> : days.map((group) => <div key={group.day || "none"} style={{ marginTop: "12px" }}>
			<div style={{ fontWeight: 700, fontSize: "13px", padding: "4px 0", borderBottom: "2px solid var(--wa-filo)" }}>{dayTitle(group.day)}</div>
			{group.rows.map((event, index) => <div key={event.id + ":" + event.action + ":" + index} style={{ display: "flex", gap: "10px", alignItems: "flex-start", padding: "7px 0", borderBottom: "1px solid var(--wa-filo)", fontSize: "13px" }}>
				<div style={{ flex: "0 0 66px", color: "var(--wa-apagado)", fontSize: "12px", whiteSpace: "nowrap" }}>{event.at ? event.at.split(" ").slice(1).join(" ") : ""}</div>
				<div style={{ flex: "1 1 auto", minWidth: 0 }}>
					<div>{event.user ? <><span style={{ fontWeight: 600 }}>{event.user}</span> · </> : null}{label(event, !!event.product && firstOfPending[event.product.id] === event.id)}</div>
					{event.product ? <div style={{ overflowWrap: "anywhere" }}>{event.product.name}{event.product.size && !event.product.name.includes(event.product.size) ? " · " + event.product.size : ""}{event.product.color ? " · " + event.product.color : ""}
						<span style={ui.tiny}>{" "}{event.product.pending ? "por dar de alta" : ""}{event.product.code ? (event.product.pending ? " · " : "") + event.product.code : ""}</span></div> : null}
					{event.detail ? <div style={{ ...ui.small, overflowWrap: "anywhere" }}>{event.detail}</div> : null}
				</div>
				<div style={{ flex: "0 0 auto", textAlign: "right", whiteSpace: "nowrap" }}>
					{event.delta !== 0 ? <div style={{ fontWeight: 700, color: event.delta < 0 ? "var(--wa-acento-texto)" : "var(--wa-tinta)" }}>{event.delta > 0 ? "+" + event.delta : "−" + Math.abs(event.delta)}</div> : null}
					{event.product && event.after !== null ? <div style={ui.tiny}>quedan {event.after}</div> : null}
				</div>
			</div>)}
		</div>)}
		{filtered.length > shown ? <div style={{ marginTop: "10px", textAlign: "center" }}>
			<button type="button" className={ui.btn} onClick={() => setShown(shown + PAGE)}>Ver {Math.min(PAGE, filtered.length - shown)} Más</button></div> : null}
	</div>;
};

export default HistorialConteo;
